import { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add24Regular, Edit20Regular } from "@fluentui/react-icons";
import { normalizzaSigla } from "@app/schema";
import { useData } from "../data/DataProvider";
import { useServizi } from "../data/useServizi";
import { salvaServizio, type Servizio } from "../data/gare";
import { goBack } from "../lib/nav";
import { Shell } from "../components/Shell";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  list: { display: "flex", flexDirection: "column", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground1 },
  row: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, borderBottom: `1px solid ${tokens.colorNeutralStroke3}` },
  sigla: { minWidth: "56px", fontWeight: tokens.fontWeightSemibold, fontFamily: tokens.fontFamilyMonospace },
  body: { flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
});

/** Servizi e sigle (solo gestori). Non si eliminano: si disattivano. */
export function Servizi() {
  const s = useStyles();
  const { servizi, error, reload } = useServizi();
  const [edit, setEdit] = useState<{ servizio?: Servizio }>();

  return (
    <Shell
      title="Servizi e sigle"
      onBack={() => goBack()}
      actions={
        <Button appearance="primary" icon={<Add24Regular />} onClick={() => setEdit({})}>
          Nuovo
        </Button>
      }
    >
      <span className={s.meta}>La sigla entra nel nome delle cartelle di gara. Un servizio non più usato si disattiva: resta collegato alle gare passate.</span>
      {error ? (
        <ErrorState error={error} />
      ) : !servizi ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : (
        <div className={s.list}>
          {servizi.map((x) => (
            <div key={x.id} className={s.row}>
              <span className={s.sigla}>{x.fields.Sigla}</span>
              <div className={s.body}>
                <span>{x.fields.Title}</span>
                {x.fields.Tipologia && <span className={s.meta}>{x.fields.Tipologia}</span>}
              </div>
              {x.fields.Attivo === false && (
                <Badge appearance="outline" color="subtle">
                  Non attivo
                </Badge>
              )}
              <Button appearance="subtle" icon={<Edit20Regular />} aria-label={`Modifica ${x.fields.Sigla}`} onClick={() => setEdit({ servizio: x })} />
            </div>
          ))}
        </div>
      )}
      {edit && servizi && (
        <ServizioDialog
          servizio={edit.servizio}
          tutti={servizi}
          onClose={(saved) => {
            setEdit(undefined);
            if (saved) void reload();
          }}
        />
      )}
    </Shell>
  );
}

function ServizioDialog(props: { servizio?: Servizio; tutti: Servizio[]; onClose: (saved: boolean) => void }) {
  const s = useStyles();
  const data = useData();
  const f = props.servizio?.fields ?? {};
  const [nome, setNome] = useState(f.Title ?? "");
  const [sigla, setSigla] = useState(f.Sigla ?? "");
  const [tipologia, setTipologia] = useState(f.Tipologia ?? "");
  const [attivo, setAttivo] = useState(f.Attivo !== false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  async function save() {
    setBusy(true);
    setErr(undefined);
    try {
      await salvaServizio(data, { Title: nome, Sigla: sigla, Tipologia: tipologia, Attivo: attivo }, props.servizio);
      props.onClose(true);
    } catch (e) {
      setErr(describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{props.servizio ? `Servizio ${f.Sigla}` : "Nuovo servizio"}</DialogTitle>
          <DialogContent className={s.form}>
            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <Field label="Servizio" required>
              <Input value={nome} onChange={(_, d) => setNome(d.value)} />
            </Field>
            <Field label="Sigla" required hint={`Spazi e trattini bassi diventano trattini${sigla.trim() ? `: ${normalizzaSigla(sigla)}` : ""}. Cambiarla non rinomina le cartelle già create.`}>
              <Input value={sigla} onChange={(_, d) => setSigla(d.value.toUpperCase())} />
            </Field>
            <Field label="Tipologia">
              <Input value={tipologia} onChange={(_, d) => setTipologia(d.value)} />
            </Field>
            <Switch label="Attivo (proposto nelle nuove gare)" checked={attivo} onChange={(_, d) => setAttivo(d.checked)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => props.onClose(false)} disabled={busy}>
              Annulla
            </Button>
            <Button appearance="primary" onClick={save} disabled={busy}>
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
